import * as React from 'react'
import { ReactElement, useCallback, useEffect } from 'react'
import { DialogActions, DialogBody, DialogTitle, DialogTrigger, makeStyles, mergeClasses } from '@fluentui/react-components'
import useAxios from '../../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'
import { HammockFormDialogProps } from '../HammockFormDialog'
import { TimeMutationDto } from '../../../../config/registrationConfig'
import { FormikConfig, useFormikContext } from 'formik'
import { bundleIcon, DismissFilled, EditOffFilled, EditOffRegular, SaveFilled, SaveRegular } from '@fluentui/react-icons'
import { useApplicationStyles } from '../../../../pages/App'
import TimeMutationDialogBodyContent from './TimeMutationDialogBodyContent'
import TooltippedButton from '../../Button/TooltippedButton'
import { useCounter } from 'usehooks-ts'
import { TimeSuggestionStatus } from '../../../../pages/Time/BaseTimeSuggestionOverview'
import { markCanceled } from '../../../../helpers/aiCancelFilter'

const CloseIcon = bundleIcon(DismissFilled, DismissFilled)
const DontTrackIcon = bundleIcon(EditOffFilled, EditOffRegular)
const TrackIcon = bundleIcon(SaveFilled, SaveRegular)

const useStyles = makeStyles({
    actions: {
        width: '100%',
    },
})

export type TrackTimeSuggestionDto = TimeMutationDto & {
    timeSuggestionId: string
}

export default function TimeSuggestionMutationDialogBody(props: HammockFormDialogProps<TrackTimeSuggestionDto>): ReactElement {
    const axios = useAxios()

    const appStyles = useApplicationStyles()
    const styles = useStyles()

    const { t: translate } = useTranslation()

    const { count: focusTrigger, increment: updateFocusTrigger } = useCounter(0)
    const { values, submitForm, isSubmitting, setSubmitting, resetForm } = useFormikContext<TrackTimeSuggestionDto>()

    /**
     * Runs one of the two actions that are not a Formik submit. They still have to flip isSubmitting, because that
     * is what disables every button in this footer; without it those two stayed clickable for the whole request
     * and a second press sent the same suggestion again, which the server then rejected.
     *
     * The dialog is closed whatever the outcome. Leaving it open on a failure invited exactly that second press.
     */
    const runAction = useCallback(
        (url: string, send: () => Promise<unknown>) => {
            setSubmitting(true)
            return send()
                .catch(error => {
                    // The dialog unmounts on dismiss, aborting this request. AppInsights logs that abort as a
                    // status-0 dependency failure unless we mark it.
                    if (error?.message === 'canceled' || error?.code === 'ERR_CANCELED') {
                        markCanceled(url)
                    }
                })
                .finally(() => {
                    setSubmitting(false)
                    resetForm()
                    props.onDismiss()
                })
        },
        [props.onDismiss, resetForm, setSubmitting],
    )

    const handleSubmit = useCallback<FormikConfig<TrackTimeSuggestionDto>['onSubmit']>(
        values => axios.post(`/time-suggestion/${values.timeSuggestionId}/time`, values, { headers: { SuppressErrors: 'true' } }),
        [],
    )

    useEffect(() => {
        props.setOnSubmit?.(handleSubmit)
    }, [handleSubmit])

    useEffect(() => updateFocusTrigger(), [])

    return (
        <DialogBody>
            <DialogTitle>{translate('time.suggestion.title')}</DialogTitle>
            <TimeMutationDialogBodyContent {...props} focusTrigger={focusTrigger} updateFocusTrigger={updateFocusTrigger} />
            <DialogActions>
                <div className={mergeClasses(styles.actions, appStyles.spaceBetween)}>
                    <DialogTrigger disableButtonEnhancement>
                        <TooltippedButton
                            appearance="secondary"
                            label={translate('action.cancel')}
                            tooltip={translate('time.suggestion.cancel.tooltip')}
                            icon={<CloseIcon />}
                            onClick={() => {
                                const url = `/time-suggestion/${values.timeSuggestionId}/ignore`
                                runAction(url, () => axios.get(url, { headers: { SuppressErrors: 'true' } }))
                            }}
                            disabled={isSubmitting}
                        />
                    </DialogTrigger>
                    <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
                        <TooltippedButton
                            appearance="primary"
                            label={translate('time.suggestion.track')}
                            icon={<TrackIcon />}
                            onClick={() => submitForm()}
                            disabled={isSubmitting}
                        />
                        <TooltippedButton
                            appearance="subtle"
                            label={translate('time.suggestion.donttrack')}
                            tooltip={translate('time.suggestion.donttrack.tooltip')}
                            icon={<DontTrackIcon />}
                            onClick={() => {
                                const url = `/time-suggestion/${values.timeSuggestionId}`
                                runAction(url, () =>
                                    axios.put(url, { status: TimeSuggestionStatus.NoTracking }, { headers: { SuppressErrors: 'true' } }),
                                )
                            }}
                            disabled={isSubmitting}
                        />
                    </div>
                </div>
            </DialogActions>
        </DialogBody>
    )
}
